import * as THREE from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import { clone as cloneSkinned } from "three/examples/jsm/utils/SkeletonUtils.js";
import { headingRadians } from "../core/direction";
import type { Direction, GhostName } from "../core/types";
import { CAMERA_FACING_Y, FLOOR_Y, TILE } from "./layout";

/**
 * The two characters, loaded from the Blender exports.
 *
 * Both GLBs are skinned, so every AI Slop is a `SkeletonUtils.clone` of one
 * load -- a plain `Object3D.clone` shares the skeleton and makes all four move
 * as one puppet.
 */

/**
 * Served straight out of `public/`, so they are copied verbatim into `dist/`
 * rather than pulled through the module graph. `BASE_URL` keeps them correct
 * under the relative `base` this app builds with.
 */
const MODEL_URLS = {
  astro: `${import.meta.env.BASE_URL}models/astro.glb`,
  sloppy: `${import.meta.env.BASE_URL}models/sloppy.glb`,
} as const;

/** Blender units to world units. Measured from the exported bounds. */
const ASTRO_SCALE = 1.0; // 1.88 units tall in a 2-unit corridor
const SLOPPY_SCALE = 0.46; // ~4.8 units tall unscaled: too big to fit a hallway

const SLOP_TINTS: Readonly<Record<GhostName, number>> = {
  blinky: 0xff4d4d,
  pinky: 0xff8ad4,
  inky: 0x5fe0ff,
  clyde: 0xffb14d,
};

const FRIGHTENED_TINT = 0x2b3cff;

/**
 * Both characters are authored facing -Y in Blender, which the Y-up glTF export
 * turns into +Z. `headingRadians` assumes a model whose forward is +X, so every
 * model gets a quarter turn to line the two conventions up. Without it the
 * astronaut walks permanently sideways to the way it is heading.
 */
const MODEL_FORWARD_OFFSET = Math.PI / 2;

/** The colour the astronaut glows while a 5h Quota Reset is active. */
const EMPOWERED_GLOW = new THREE.Color(0xffc24d);

/**
 * Which baked clip plays for each moment.
 *
 * All three are sections of the same capture, cut at different points; naming
 * them here keeps the mapping in one place rather than scattered through the
 * pose logic.
 */
const POWER_UP_CLIP = "Victory";
const CATCH_CLIP = "Victory2";
const LEVEL_CLEAR_CLIP = "Victory3";

/**
 * Clips that turn out to the camera instead of tracking the heading.
 *
 * Only the level-clear dance qualifies. It is a performance: the deck is done,
 * the astronaut has stopped, and there is no direction of travel left to face.
 *
 * The other two celebrations play *while the astronaut is still driving* — the
 * power-up loops for the whole Quota Reset window, and the catch fires
 * mid-chase — so pinning them to the camera slides the body sideways and
 * backwards down the corridors. Those steer by heading, exactly like the walk.
 * Every clip is baked facing-neutral (MODEL_REST_FORWARD in
 * tools/export_astro.py), so this rotation is the only thing aiming any of them.
 */
const CAMERA_FACING_CLIPS = new Set([LEVEL_CLEAR_CLIP]);

export interface LoadedModels {
  readonly astro: THREE.Group;
  readonly astroClips: THREE.AnimationClip[];
  readonly sloppy: THREE.Group;
  readonly sloppyClips: THREE.AnimationClip[];
}

export const loadModels = async (): Promise<LoadedModels> => {
  const loader = new GLTFLoader();
  const [astro, sloppy] = await Promise.all([loader.loadAsync(MODEL_URLS.astro), loader.loadAsync(MODEL_URLS.sloppy)]);
  return {
    astro: astro.scene,
    astroClips: astro.animations,
    sloppy: sloppy.scene,
    sloppyClips: sloppy.animations,
  };
};

/**
 * Force the mixer to bind every clip before the first one is needed.
 *
 * three.js resolves an AnimationAction's property bindings lazily, on the first
 * update that gives it weight. For a long clip over a 17-bone skin that is a
 * visible hitch, and it lands exactly when something interesting happens -- the
 * first catch, the first death. Playing each action once at zero weight does
 * the binding work up front, during load, where nobody is waiting on it.
 */
const warmClips = (mixer: THREE.AnimationMixer, actions: readonly THREE.AnimationAction[]): void => {
  for (const action of actions) {
    action.reset();
    action.enabled = true;
    action.setEffectiveWeight(0);
    action.play();
  }
  mixer.update(0);
  for (const action of actions) {
    action.stop();
    action.setEffectiveWeight(1);
  }
};

const enableShadows = (root: THREE.Object3D): void => {
  root.traverse((node) => {
    if (node instanceof THREE.Mesh) {
      node.castShadow = true;
      node.receiveShadow = true;
      node.frustumCulled = false; // skinned bounds go stale mid-animation
    }
  });
};

/** The player's astronaut, with its walk/idle/tumble clips wired up. */
export class AstroActor {
  readonly object: THREE.Group;

  private readonly mixer: THREE.AnimationMixer;
  private readonly actions = new Map<string, THREE.AnimationAction>();
  private current = "";
  private readonly pivot: THREE.Group;
  private readonly suitMaterials: THREE.MeshStandardMaterial[] = [];
  private readonly halo: THREE.PointLight;
  private empowered = false;
  private urgency = 0;
  private glowPhase = 0;
  private clock = 0;
  private oneShotUntil = 0;

  constructor(models: LoadedModels) {
    this.pivot = new THREE.Group();
    this.object = cloneSkinned(models.astro) as THREE.Group;
    this.object.scale.setScalar(ASTRO_SCALE);
    this.object.rotation.y = MODEL_FORWARD_OFFSET;
    enableShadows(this.object);

    // Clone the suit materials: the GLB shares them with nothing else here, but
    // the glow is per-actor state and must not leak into any other instance.
    this.object.traverse((node) => {
      if (!(node instanceof THREE.Mesh)) return;
      const list = Array.isArray(node.material) ? node.material : [node.material];
      const cloned = list.map((m) => {
        const copy = (m as THREE.Material).clone() as THREE.MeshStandardMaterial;
        this.suitMaterials.push(copy);
        return copy;
      });
      node.material = Array.isArray(node.material) ? cloned : cloned[0]!;
    });

    this.halo = new THREE.PointLight(EMPOWERED_GLOW.getHex(), 0, TILE * 5, 2);
    this.halo.position.y = 1.1;
    this.halo.visible = false;

    const holder = new THREE.Group();
    holder.add(this.object);
    this.pivot.add(holder);
    this.pivot.add(this.halo);

    this.mixer = new THREE.AnimationMixer(this.object);
    for (const clip of models.astroClips) {
      const action = this.mixer.clipAction(clip);
      this.actions.set(clip.name, action);
    }
    warmClips(this.mixer, [...this.actions.values()]);
    this.play("Idle");
  }

  /** The node to add to the scene. */
  get root(): THREE.Group {
    return this.pivot;
  }

  play(name: string, once = false): void {
    if (this.current === name) return;
    const next = this.actions.get(name);
    if (next === undefined) return;
    const prev = this.actions.get(this.current);
    next.reset();
    next.setLoop(once ? THREE.LoopOnce : THREE.LoopRepeat, once ? 1 : Infinity);
    next.clampWhenFinished = once;
    next.enabled = true;
    next.setEffectiveWeight(1);
    if (prev !== undefined) next.crossFadeFrom(prev, 0.18, false);
    next.play();
    this.current = name;
  }

  /**
   * Make the astronaut glow while a 5h Quota Reset is running.
   *
   * `empowered` drives an emissive pulse across every suit material, so the
   * moment the slop become edible is unmistakable from the astronaut alone --
   * you should not have to read the HUD to know you are the one hunting.
   * `urgency` rises as the window runs out and makes the pulse beat faster.
   */
  setEmpowered(empowered: boolean, urgency: number): void {
    this.empowered = empowered;
    this.urgency = urgency;
  }

  private applyGlow(dt: number): void {
    this.glowPhase += dt * (this.empowered ? 6 + this.urgency * 12 : 6);
    const wave = this.empowered ? 0.55 + 0.45 * Math.sin(this.glowPhase) : 0;
    for (let i = 0; i < this.suitMaterials.length; i += 1) {
      const mat = this.suitMaterials[i]!;
      mat.emissive.copy(EMPOWERED_GLOW);
      mat.emissiveIntensity = wave * 1.5;
    }
    this.halo.visible = this.empowered;
    if (this.empowered) {
      this.halo.intensity = 14 + wave * 26;
      this.halo.distance = TILE * (3 + wave * 2.5);
    }
  }

  setPose(x: number, y: number, dir: Direction, moving: boolean): void {
    this.pivot.position.set(x * TILE, FLOOR_Y, y * TILE);
    // Only the level-clear dance breaks off to face the camera; everything
    // else, celebrating or not, points where the astronaut is going.
    const target = CAMERA_FACING_CLIPS.has(this.current) ? CAMERA_FACING_Y : headingRadians(dir);
    // Shortest-arc turn, so walking left after walking right does not spin the
    // astronaut the long way around.
    const delta = Math.atan2(Math.sin(target - this.pivot.rotation.y), Math.cos(target - this.pivot.rotation.y));
    this.pivot.rotation.y += delta * 0.35;

    // Clip priority, highest first: being Slopped and clearing the deck both
    // own the body outright; a running catch celebration holds it for its own
    // length; the power-up dance loops for as long as the window lasts; and
    // locomotion is what is left.
    if (this.current === "Tumble" || this.current === LEVEL_CLEAR_CLIP) return;
    if (this.clock < this.oneShotUntil) return;
    if (this.empowered) {
      this.play(POWER_UP_CLIP);
      return;
    }
    this.play(moving ? "Walk" : "Idle");
  }

  tumble(): void {
    this.play("Tumble", true);
  }

  /** Level cleared: dance, and keep dancing. */
  celebrate(): void {
    this.play(LEVEL_CLEAR_CLIP);
  }

  /**
   * Caught a Slop while powered up.
   *
   * Held for the clip's own length rather than a hardcoded guess, then
   * released back to the power-up dance or the walk. A fresh catch restarts
   * it, so catching several in one window reads as repeated celebration.
   */
  chomp(): void {
    if (this.current === "Tumble" || this.current === LEVEL_CLEAR_CLIP) return;
    this.play(CATCH_CLIP, true);
    this.oneShotUntil = this.clock + this.clipSeconds(CATCH_CLIP);
  }

  /** A clip's real duration, so hold times cannot drift from the asset. */
  private clipSeconds(name: string): number {
    return this.actions.get(name)?.getClip().duration ?? 1;
  }

  /**
   * Back on your feet after being Slopped.
   *
   * `Tumble` is LoopOnce with `clampWhenFinished`, so once it ends it holds its
   * final collapsed pose at full weight forever. Simply starting `Idle` layers
   * the two and the astronaut respawns still folded up -- the clip has to be
   * stopped and rewound, not merely faded away from. Blanking `current` first
   * made it worse, because `play` then found no previous action to cross-fade.
   */
  revive(): void {
    this.mixer.stopAllAction();
    for (const action of this.actions.values()) {
      action.reset();
      action.enabled = false;
      action.setEffectiveWeight(0);
    }
    this.object.traverse((node) => {
      if (node instanceof THREE.SkinnedMesh) node.skeleton.pose(); // back to bind pose
    });
    this.current = "";
    this.oneShotUntil = 0;
    this.play("Idle");
  }

  /** Mixer state, so a test can prove no stale clip is still deforming us. */
  animationState(): { current: string; weights: Record<string, number>; facing: number } {
    const weights: Record<string, number> = {};
    for (const [name, action] of this.actions) {
      weights[name] = action.isRunning() ? action.getEffectiveWeight() : 0;
    }
    // `facing` is the yaw actually applied to the body, so a test can check
    // that a celebration steers with the heading rather than trusting the eye.
    return { current: this.current, weights, facing: this.pivot.rotation.y };
  }

  update(dt: number): void {
    this.clock += dt;
    this.mixer.update(dt);
    this.applyGlow(dt);
  }
}

/** One AI Slop. Four of these share a single loaded GLB. */
export class SlopActor {
  readonly root = new THREE.Group();
  readonly name: GhostName;

  private readonly mixer: THREE.AnimationMixer;
  private readonly materials: THREE.MeshStandardMaterial[] = [];
  private readonly baseColours: THREE.Color[] = [];
  private readonly body: THREE.Group;
  private bob = 0;

  constructor(models: LoadedModels, name: GhostName) {
    this.name = name;
    this.body = cloneSkinned(models.sloppy) as THREE.Group;
    this.body.scale.setScalar(SLOPPY_SCALE);
    this.body.rotation.y = MODEL_FORWARD_OFFSET;
    enableShadows(this.body);
    this.root.add(this.body);

    // Tint each slop so the four are tellable apart, exactly as the arcade's
    // four ghost colours do. Clone the materials first or the tint leaks.
    const tint = new THREE.Color(SLOP_TINTS[name]);
    this.body.traverse((node) => {
      if (!(node instanceof THREE.Mesh)) return;
      const source = node.material as THREE.Material | THREE.Material[];
      const list = Array.isArray(source) ? source : [source];
      const cloned = list.map((m) => {
        const copy = m.clone() as THREE.MeshStandardMaterial;
        if (copy.color !== undefined) copy.color.lerp(tint, 0.45);
        this.materials.push(copy);
        this.baseColours.push(copy.color.clone());
        return copy;
      });
      node.material = Array.isArray(source) ? cloned : cloned[0]!;
    });

    this.mixer = new THREE.AnimationMixer(this.body);
    const actions = models.sloppyClips.map((clip) => this.mixer.clipAction(clip));
    warmClips(this.mixer, actions);
    for (const action of actions) action.play();
  }

  setPose(x: number, y: number, dir: Direction, opts: { frightened: boolean; eaten: boolean }): void {
    this.root.position.set(x * TILE, FLOOR_Y, y * TILE);
    const target = headingRadians(dir);
    const delta = Math.atan2(Math.sin(target - this.root.rotation.y), Math.cos(target - this.root.rotation.y));
    this.root.rotation.y += delta * 0.3;
    // Slop hovers; a bob sells that it is not walking on the deck.
    this.body.position.y = Math.sin(this.bob * 3 + x + y) * 0.08;

    for (let i = 0; i < this.materials.length; i += 1) {
      const mat = this.materials[i]!;
      const base = this.baseColours[i]!;
      if (opts.frightened) mat.color.copy(base).lerp(new THREE.Color(FRIGHTENED_TINT), 0.75);
      else mat.color.copy(base);
      mat.transparent = opts.eaten;
      mat.opacity = opts.eaten ? 0.22 : 1;
      mat.depthWrite = !opts.eaten;
    }
  }

  update(dt: number, speedScale: number): void {
    this.bob += dt;
    this.mixer.update(dt * speedScale);
  }
}
